// src/components/AuthModal.jsx
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { AUTH_ENDPOINTS, QR_ENDPOINTS, API_URL } from "../config";
import { useMobile } from "../styles/commonHooks";
import { getThemeVars, globalAnimationStyles } from "../styles/theme";

// FastAPI может вернуть detail как строку, объект или массив объектов Pydantic.
// Если передать объект/массив напрямую в setState → React #31 crash при рендере.
function parseApiError(detail, fallback = "Error") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(", ");
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || detail.detail || JSON.stringify(detail);
  }
  return String(detail);
}

export default function AuthModal({
  isOpen,
  onClose,
  lang = "en",
  theme = "dark",
  translations,
  onLogin,
  eventInviteCode = null,
  initialMode = "login"
}) {
  const [activeTab, setActiveTab] = useState("login");
  const isMobile = useMobile();
  const themeVars = getThemeVars(theme);

  useEffect(() => {
    if (initialMode === "register") {
      setActiveTab("register");
    } else {
      setActiveTab("login");
    }
  }, [initialMode]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <style>{globalAnimationStyles}</style>
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.65)",
          backdropFilter: "blur(8px)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "20px 16px" : "0 24px",
          animation: "fadeIn 0.25s ease-out",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: isMobile ? "460px" : "580px",
            background: `linear-gradient(135deg, ${themeVars.subtleBg} 0%, ${themeVars.subtleBg}99 100%)`,
            backdropFilter: "blur(16px) saturate(200%) contrast(1.1)",
            WebkitBackdropFilter: "blur(16px) saturate(200%) contrast(1.1)",
            borderRadius: "20px",
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.37),
              0 0 64px rgba(0, 0, 0, 0.25),
              inset 0 0 2px rgba(255, 255, 255, 0.1)
            `,
            border: `1px solid rgba(255, 255, 255, 0.08)`,
            maxHeight: "95vh",
            overflowY: "auto",
            position: "relative",
            animation: "fadeInUp 0.35s ease-out",
            pointerEvents: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: isMobile ? "24px 16px 0" : "32px 24px 0",
              borderBottom: `1px solid ${themeVars.border}`,
              position: "relative",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                background: "rgba(255, 255, 255, 0.08)",
                borderRadius: "999px",
                padding: "4px",
                border: `1px solid ${themeVars.border}`,
                position: "relative",
                overflow: "hidden",
                backdropFilter: "blur(10px)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "5px",
                  bottom: "5px",
                  left: "5px",
                  width: "calc(50% - 10px)",
                  background: "linear-gradient(90deg, #6366f1, #a78bfa, #ec4899)",
                  borderRadius: "999px",
                  transition: "transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  transform: activeTab === "login" ? "translateX(0)" : "translateX(100%)",
                  boxShadow: "0 0 20px rgba(99, 102, 241, 0.6)",
                  zIndex: 1,
                }}
              />
              
              <button 
                onClick={() => setActiveTab("login")}
                style={{
                  padding: isMobile ? "10px 24px" : "12px 32px",
                  background: "transparent",
                  color: activeTab === "login" ? "white" : themeVars.fg + "aa",
                  border: "none",
                  borderRadius: "999px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.95rem" : "1rem",
                  transition: "color 0.3s ease",
                  position: "relative",
                  zIndex: 2,
                  whiteSpace: "nowrap",
                  ...(activeTab === "login" && {
                    textShadow: "0 0 10px rgba(255,255,255,0.7)"
                  }),
                }}
              >
                {translations[lang]?.login || "Sign in"}
              </button>
              
              <button
                onClick={() => setActiveTab("register")}
                style={{
                  padding: isMobile ? "10px 24px" : "12px 32px",
                  background: "transparent",
                  color: activeTab === "register" ? "white" : themeVars.fg + "aa",
                  border: "none",
                  borderRadius: "999px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.95rem" : "1rem",
                  transition: "color 0.3s ease",
                  position: "relative",
                  zIndex: 2,
                  whiteSpace: "nowrap",
                  ...(activeTab === "register" && {
                    textShadow: "0 0 10px rgba(255,255,255,0.7)"
                  }),
                }}
              >
                {translations[lang]?.register || "Register"}
              </button>
            </div>
          </div>

          <div 
            style={{ 
              padding: isMobile ? "24px 16px" : "32px 24px",
              height: isMobile ? "570px" : "670px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {activeTab === "login" ? (
              <LoginForm 
                lang={lang} 
                theme={theme} 
                translations={translations} 
                onLogin={onLogin} 
                onClose={onClose}
              />
            ) : (
              <RegisterForm 
                lang={lang} 
                theme={theme} 
                translations={translations} 
                onLogin={onLogin} 
                onClose={onClose}
                eventInviteCode={eventInviteCode}
              />
            )}
          </div>

          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: isMobile ? "16px" : "24px",
              right: isMobile ? "16px" : "24px",
              background: "rgba(255, 255, 255, 0.12)",
              border: "none",
              borderRadius: "12px",
              width: isMobile ? "32px" : "36px",
              height: isMobile ? "32px" : "36px",
              color: themeVars.fg,
              fontSize: "1.4rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              zIndex: 100,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.12)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            ×
          </button>
        </div>
      </div>
    </>
  );
}

function LoginForm({ lang, theme, translations, onLogin, onClose }) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const isMobile = useMobile();
  const themeVars = getThemeVars(theme);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    const providerFromUrl = searchParams.get("provider");
    const actionFromUrl = searchParams.get("action");
    const errorFromUrl = searchParams.get("error");

    if (tokenFromUrl && providerFromUrl && actionFromUrl === "login") {
      onLogin(tokenFromUrl);
      onClose();
      return;
    }

    if (errorFromUrl) {
      const messageFromUrl = searchParams.get("message");
      setError(messageFromUrl || translations[lang].oauthError || "OAuth authorization failed");
    }
  }, [searchParams, onLogin, onClose, translations, lang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError(translations[lang]?.emailRequired || "Email is required");
      return;
    }
    if (!password.trim()) {
      setError(translations[lang]?.passwordRequired || "Password is required");
      return;
    }

    try {
      const loginData = new URLSearchParams();
      loginData.append("username", email);
      loginData.append("password", password);
      
      const res = await axios.post(`${API_URL}${AUTH_ENDPOINTS.LOGIN}`, loginData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      
      const token = res.data.access_token;
      if (!token) throw new Error("Login failed");
      
      onLogin(token);
      onClose();
    } catch (err) {
      let msg = translations[lang]?.error || "Error";
      if (err.response?.data?.detail) msg = parseApiError(err.response.data.detail, msg);
      setError(msg);
    }
  };

  const handleSocialLogin = (provider) => {
    window.location.href = `/api/auth/${provider}/login?mode=login`;
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "20px" : "28px",
        width: "100%",
        maxWidth: "480px",
      }}
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={translations[lang]?.email || "Email"}
        autoComplete="email"
        style={{
          background: "rgba(255, 255, 255, 0.09)",
          color: themeVars.fg,
          border: "none",
          borderRadius: "14px",
          padding: "15px 20px",
          fontSize: "1.08rem",
          outline: "none",
          transition: "all 0.28s ease",
        }}
        onFocus={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.16)";
          e.target.style.boxShadow = `0 0 0 3px ${themeVars.accent}50`;
        }}
        onBlur={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.09)";
          e.target.style.boxShadow = "none";
        }}
      />

      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={translations[lang]?.password || "Password"}
        autoComplete="current-password"
        style={{
          background: "rgba(255, 255, 255, 0.09)",
          color: themeVars.fg,
          border: "none",
          borderRadius: "14px",
          padding: "15px 20px",
          fontSize: "1.08rem",
          outline: "none",
          transition: "all 0.28s ease",
        }}
        onFocus={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.16)";
          e.target.style.boxShadow = `0 0 0 3px ${themeVars.accent}50`;
        }}
        onBlur={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.09)";
          e.target.style.boxShadow = "none";
        }}
      />

      <button
        type="submit"
        style={{
          width: "75%",
          maxWidth: 580,
          margin: "8px auto 0",
          border: "none",
          borderRadius: 36,
          overflow: "hidden",
          position: "relative",
          cursor: "pointer",
          background: "transparent",
          padding: 0,
          isolation: "isolate",
          transition: "transform 0.4s ease-out, box-shadow 0.4s ease-out",
          boxShadow: "0 25px 90px rgba(99, 102, 241, 0.3)",
          display: "block",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-12px) scale(1.022)";
          e.currentTarget.style.boxShadow = "0 50px 140px rgba(99, 102, 241, 0.55)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0) scale(1)";
          e.currentTarget.style.boxShadow = "0 25px 90px rgba(99, 102, 241, 0.3)";
        }}
      >
        <div
          className="lava-core"
          style={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(circle at 30% 40%, #10b981 0%, transparent 40%),
              radial-gradient(circle at 70% 60%, #6366f1 0%, transparent 45%),
              radial-gradient(circle at 20% 80%, #8b5cf6 0%, transparent 38%),
              radial-gradient(circle at 80% 30%, #ec4899 0%, transparent 42%),
              linear-gradient(160deg, #22d3ee 0%, #c084fc 100%)
            `,
            backgroundSize: "180% 180%, 200% 200%, 220% 220%, 190% 190%, 300% 300%",
            backgroundPosition: "0% 0%, 100% 100%, 50% 0%, 0% 100%, 50% 50%",
            opacity: 0.75,
            animation: "lavaDrift 38s linear infinite",
            filter: "blur(4px) saturate(1.4)",
            mixBlendMode: "screen",
          }}
        />

        <div
          className="lava-overlay"
          style={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(circle at 55% 25%, #10b98188 0%, transparent 50%),
              radial-gradient(circle at 15% 65%, #6366f188 0%, transparent 55%),
              radial-gradient(circle at 85% 75%, #8b5cf688 0%, transparent 48%)
            `,
            backgroundSize: "240% 240%, 280% 280%, 260% 260%",
            opacity: 0.6,
            animation: "lavaDrift2 52s linear infinite reverse",
            filter: "blur(6px)",
            mixBlendMode: "overlay",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 4,
            padding: isMobile ? "15px" : "17px",
            color: "#ffffff",
            textShadow: "0 2px 12px rgba(0, 0, 0, 0.7)",
            fontSize: "1.12rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {translations[lang]?.login || "Sign in"}
        </div>
      </button>

      {error && (
        <div style={{ color: "#f87171", textAlign: "center", marginTop: "14px", fontSize: "0.96rem" }}>
          {error}
        </div>
      )}

      <div
        style={{
          margin: isMobile ? "12px 0 8px" : "36px 0 20px",
          textAlign: "center",
          opacity: 0.78,
          fontSize: "0.96rem",
        }}
      >
        {translations[lang]?.or_sign_in_with || "or sign in with"}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "row" : "column",
          gap: isMobile ? "10px" : "16px",
          justifyContent: isMobile ? "center" : "stretch",
          alignItems: isMobile ? "center" : "stretch",
        }}
      >
        <button
          type="button"
          onClick={() => handleSocialLogin("google")}
          style={{
            background: "rgba(255, 255, 255, 0.09)",
            border: "none",
            borderRadius: "12px",
            padding: isMobile ? "10px 16px" : "14px 20px",
            fontSize: isMobile ? "0.96rem" : "1.02rem",
            color: themeVars.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            cursor: "pointer",
            transition: "all 0.22s ease",
            flex: isMobile ? "1 1 auto" : "auto",
            minWidth: isMobile ? "0" : "auto",
            maxWidth: isMobile ? "48%" : "auto",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            if (!isMobile) {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)";
              e.currentTarget.style.transform = "scale(1.03)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isMobile) {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.09)";
              e.currentTarget.style.transform = "scale(1)";
            }
          }}
        >
          <img src="https://www.google.com/favicon.ico" alt="Google" width={18} height={18} />
          Google
        </button>

        <button
          type="button"
          onClick={() => handleSocialLogin("linkedin")}
          style={{
            background: "rgba(255, 255, 255, 0.09)",
            border: "none",
            borderRadius: "12px",
            padding: isMobile ? "10px 16px" : "14px 20px",
            fontSize: isMobile ? "0.96rem" : "1.02rem",
            color: themeVars.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            cursor: "pointer",
            transition: "all 0.22s ease",
            flex: isMobile ? "1 1 auto" : "auto",
            minWidth: isMobile ? "0" : "auto",
            maxWidth: isMobile ? "48%" : "auto",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            if (!isMobile) {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)";
              e.currentTarget.style.transform = "scale(1.03)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isMobile) {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.09)";
              e.currentTarget.style.transform = "scale(1)";
            }
          }}
        >
          <img
            src="https://static.licdn.com/sc/h/al2o9zrvru7aqj8e1x2rzsrca"
            alt="LinkedIn"
            width={18}
            height={18}
          />
          LinkedIn
        </button>
      </div>
    </form>
  );
}

function RegisterForm({ lang, theme, translations, onLogin, onClose, eventInviteCode }) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [inviteCodeValid, setInviteCodeValid] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const inviteCodeRef = useRef(null);
  const isMobile = useMobile();
  const themeVars = getThemeVars(theme);

  useEffect(() => {
    if (eventInviteCode && eventInviteCode.trim()) {
      console.log(`🎯 [AuthModal] Auto-filling invite code from event: "${eventInviteCode}"`);
      setInviteCode(eventInviteCode);
      setIsAutoFilled(true);
      validateInviteCode(eventInviteCode);

      setTimeout(() => {
        if (inviteCodeRef.current) {
          inviteCodeRef.current.focus();
          console.log(`✅ [AuthModal] Invite code field focused and filled`);
        }
      }, 100);
      return;
    }

    const codeFromUrl = searchParams.get("invite_code");
    if (codeFromUrl) {
      console.log(`📥 [AuthModal] Invite code from URL: "${codeFromUrl}"`);
      setInviteCode(codeFromUrl);
      localStorage.setItem("inviteCode", codeFromUrl);
      validateInviteCode(codeFromUrl);
      return;
    }

    const savedCode = localStorage.getItem("inviteCode");
    if (savedCode) {
      console.log(`💾 [AuthModal] Invite code from localStorage: "${savedCode}"`);
      setInviteCode(savedCode);
      validateInviteCode(savedCode);
    }
  }, [eventInviteCode, searchParams]);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError(translations[lang]?.emailRequired || "Email is required");
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError(translations[lang]?.invalidEmail || "Invalid email format");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validateInviteCode = async (code) => {
    if (!code || !code.trim()) {
      setInviteCodeValid(false);
      return false;
    }
    try {
      console.log(`🔍 [AuthModal] Validating invite code: "${code}"`);
      const res = await axios.get(QR_ENDPOINTS.CHECK_INVITE(code));
      console.log(`✅ [AuthModal] Validation result for "${code}":`, res.data.valid);
      setInviteCodeValid(res.data.valid);
      return res.data.valid;
    } catch (err) {
      console.error(`❌ [AuthModal] Validation error for "${code}":`, err);
      setInviteCodeValid(false);
      return false;
    }
  };

  useEffect(() => {
    if (!inviteCode.trim() || isAutoFilled) return;
    const id = setTimeout(() => {
      validateInviteCode(inviteCode);
    }, 500);
    return () => clearTimeout(id);
  }, [inviteCode, isAutoFilled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateEmail(email)) return;
    if (!inviteCode.trim()) {
      setError(translations[lang]?.inviteCodeRequired || "Invite code required");
      return;
    }
    if (!inviteCodeValid) {
      setError(translations[lang]?.invalidInviteCode || "Invalid invite code");
      return;
    }

    try {
      await axios.post(`${API_URL}${AUTH_ENDPOINTS.REGISTER}`, {
        email,
        password,
        username,
        name: username,
        invite_code: inviteCode,
      });

      const loginData = new URLSearchParams();
      loginData.append("username", email);
      loginData.append("password", password);

      const loginRes = await axios.post(`${API_URL}${AUTH_ENDPOINTS.LOGIN}`, loginData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const token = loginRes.data.access_token;
      if (!token) throw new Error("Auto-login failed");

      onLogin(token);
      localStorage.removeItem("inviteCode");
      onClose();
    } catch (err) {
      let msg = translations[lang]?.error || "Error";
      if (err.response?.data?.detail) msg = parseApiError(err.response.data.detail, msg);
      setError(msg);
    }
  };

  const handleSocialRegister = (provider) => {
    if (!inviteCode.trim() || !inviteCodeValid) {
      setError(translations[lang]?.inviteCodeRequired || "Invite code required");
      return;
    }
    localStorage.setItem("oauthInviteCode", inviteCode);
    window.location.href = `/api/auth/${provider}/login?mode=register&invite_code=${encodeURIComponent(inviteCode)}`;
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "20px" : "28px",
        width: "100%",
        maxWidth: "480px",
      }}
    >
      <input
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          validateEmail(e.target.value);
        }}
        placeholder={translations[lang]?.email || "Email"}
        autoComplete="email"
        style={{
          background: "rgba(255, 255, 255, 0.09)",
          color: themeVars.fg,
          border: "none",
          borderRadius: "14px",
          padding: "15px 20px",
          fontSize: "1.08rem",
          outline: "none",
          transition: "all 0.28s ease",
        }}
        onFocus={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.16)";
          e.target.style.boxShadow = `0 0 0 3px ${themeVars.accent}50`;
        }}
        onBlur={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.09)";
          e.target.style.boxShadow = "none";
        }}
      />
      {emailError && (
        <div style={{ color: "#f87171", fontSize: "0.94rem", marginTop: "-10px" }}>
          {emailError}
        </div>
      )}

      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={translations[lang]?.password || "Password"}
        autoComplete="new-password"
        style={{
          background: "rgba(255, 255, 255, 0.09)",
          color: themeVars.fg,
          border: "none",
          borderRadius: "14px",
          padding: "15px 20px",
          fontSize: "1.08rem",
          outline: "none",
          transition: "all 0.28s ease",
        }}
        onFocus={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.16)";
          e.target.style.boxShadow = `0 0 0 3px ${themeVars.accent}50`;
        }}
        onBlur={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.09)";
          e.target.style.boxShadow = "none";
        }}
      />

      <input
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder={translations[lang]?.name || "Username"}
        autoComplete="username"
        style={{
          background: "rgba(255, 255, 255, 0.09)",
          color: themeVars.fg,
          border: "none",
          borderRadius: "14px",
          padding: "15px 20px",
          fontSize: "1.08rem",
          outline: "none",
          transition: "all 0.28s ease",
        }}
        onFocus={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.16)";
          e.target.style.boxShadow = `0 0 0 3px ${themeVars.accent}50`;
        }}
        onBlur={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.09)";
          e.target.style.boxShadow = "none";
        }}
      />

      {isAutoFilled ? (
        <div
          style={{
            position: "relative",
            background: "rgba(255, 255, 255, 0.09)",
            borderRadius: "14px",
            padding: "15px 20px",
            fontSize: "1.08rem",
            color: themeVars.primary,
            fontWeight: 600,
            border: `2px solid ${themeVars.primary}80`,
            pointerEvents: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            🎫 {inviteCode}
          </span>
        </div>
      ) : (
        <input
          ref={inviteCodeRef}
          type="text"
          value={inviteCode}
          onChange={(e) => {
            setInviteCode(e.target.value);
            setIsAutoFilled(false);
          }}
          placeholder={translations[lang]?.enterInviteCode || "Invite code"}
          autoComplete="off"
          style={{
            background: "rgba(255, 255, 255, 0.09)",
            color: themeVars.fg,
            border: inviteCode.trim()
              ? inviteCodeValid
                ? "2px solid #4CAF50"
                : "2px solid #f87171"
              : "none",
            borderRadius: "14px",
            padding: "15px 20px",
            fontSize: "1.08rem",
            outline: "none",
            transition: "all 0.28s ease",
          }}
          onFocus={(e) => {
            e.target.style.background = "rgba(255, 255, 255, 0.16)";
            e.target.style.boxShadow = `0 0 0 3px ${themeVars.accent}50`;
          }}
          onBlur={(e) => {
            e.target.style.background = "rgba(255, 255, 255, 0.09)";
            e.target.style.boxShadow = "none";
          }}
        />
      )}

      {inviteCode.trim() && !isAutoFilled && (
        <div
          style={{
            color: inviteCodeValid ? "#4CAF50" : "#f87171",
            fontSize: "0.94rem",
            marginTop: "-10px",
            textAlign: "center",
          }}
        >
          {inviteCodeValid ? "Valid invite code" : "Invalid invite code"}
        </div>
      )}

      <button
        type="submit"
        style={{
          width: "75%",
          maxWidth: 580,
          margin: "8px auto 0",
          border: "none",
          borderRadius: 36,
          overflow: "hidden",
          position: "relative",
          cursor: "pointer",
          background: "transparent",
          padding: 0,
          isolation: "isolate",
          transition: "transform 0.4s ease-out, box-shadow 0.4s ease-out",
          boxShadow: "0 25px 90px rgba(99, 102, 241, 0.3)",
          display: "block",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-12px) scale(1.022)";
          e.currentTarget.style.boxShadow = "0 50px 140px rgba(99, 102, 241, 0.55)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0) scale(1)";
          e.currentTarget.style.boxShadow = "0 25px 90px rgba(99, 102, 241, 0.3)";
        }}
      >
        <div
          className="lava-core"
          style={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(circle at 30% 40%, #10b981 0%, transparent 40%),
              radial-gradient(circle at 70% 60%, #6366f1 0%, transparent 45%),
              radial-gradient(circle at 20% 80%, #8b5cf6 0%, transparent 38%),
              radial-gradient(circle at 80% 30%, #ec4899 0%, transparent 42%),
              linear-gradient(160deg, #22d3ee 0%, #c084fc 100%)
            `,
            backgroundSize: "180% 180%, 200% 200%, 220% 220%, 190% 190%, 300% 300%",
            backgroundPosition: "0% 0%, 100% 100%, 50% 0%, 0% 100%, 50% 50%",
            opacity: 0.75,
            animation: "lavaDrift 38s linear infinite",
            filter: "blur(4px) saturate(1.4)",
            mixBlendMode: "screen",
          }}
        />

        <div
          className="lava-overlay"
          style={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(circle at 55% 25%, #10b98188 0%, transparent 50%),
              radial-gradient(circle at 15% 65%, #6366f188 0%, transparent 55%),
              radial-gradient(circle at 85% 75%, #8b5cf688 0%, transparent 48%)
            `,
            backgroundSize: "240% 240%, 280% 280%, 260% 260%",
            opacity: 0.6,
            animation: "lavaDrift2 52s linear infinite reverse",
            filter: "blur(6px)",
            mixBlendMode: "overlay",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 4,
            padding: isMobile ? "15px" : "17px",
            color: "#ffffff",
            textShadow: "0 2px 12px rgba(0, 0, 0, 0.7)",
            fontSize: "1.12rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {translations[lang]?.register || "Register"}
        </div>
      </button>

      {error && (
        <div style={{ color: "#f87171", textAlign: "center", marginTop: "14px", fontSize: "0.96rem" }}>
          {error}
        </div>
      )}

      <div
        style={{
          margin: isMobile ? "12px 0 8px" : "36px 0 20px",
          textAlign: "center",
          opacity: 0.78,
          fontSize: "0.96rem",
        }}
      >
        {translations[lang]?.or_sign_up_with || "or sign up with"}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "row" : "column",
          gap: isMobile ? "10px" : "16px",
          justifyContent: isMobile ? "center" : "stretch",
          alignItems: isMobile ? "center" : "stretch",
        }}
      >
        <button
          type="button"
          onClick={() => handleSocialRegister("google")}
          style={{
            background: "rgba(255, 255, 255, 0.09)",
            border: "none",
            borderRadius: "12px",
            padding: isMobile ? "10px 16px" : "14px 20px",
            fontSize: isMobile ? "0.96rem" : "1.02rem",
            color: themeVars.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            cursor: "pointer",
            transition: "all 0.22s ease",
            flex: isMobile ? "1 1 auto" : "auto",
            minWidth: isMobile ? "0" : "auto",
            maxWidth: isMobile ? "48%" : "auto",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            if (!isMobile) {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)";
              e.currentTarget.style.transform = "scale(1.03)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isMobile) {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.09)";
              e.currentTarget.style.transform = "scale(1)";
            }
          }}
        >
          <img src="https://www.google.com/favicon.ico" alt="Google" width={18} height={18} />
          Google
        </button>

        <button
          type="button"
          onClick={() => handleSocialRegister("linkedin")}
          style={{
            background: "rgba(255, 255, 255, 0.09)",
            border: "none",
            borderRadius: "12px",
            padding: isMobile ? "10px 16px" : "14px 20px",
            fontSize: isMobile ? "0.96rem" : "1.02rem",
            color: themeVars.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            cursor: "pointer",
            transition: "all 0.22s ease",
            flex: isMobile ? "1 1 auto" : "auto",
            minWidth: isMobile ? "0" : "auto",
            maxWidth: isMobile ? "48%" : "auto",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            if (!isMobile) {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)";
              e.currentTarget.style.transform = "scale(1.03)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isMobile) {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.09)";
              e.currentTarget.style.transform = "scale(1)";
            }
          }}
        >
          <img
            src="https://static.licdn.com/sc/h/al2o9zrvru7aqj8e1x2rzsrca"
            alt="LinkedIn"
            width={18}
            height={18}
          />
          LinkedIn
        </button>
      </div>
    </form>
  );
}